/**
 * WhatsApp template helpers for the console: variables, filling previews, Meta-valid names and the creator's rule
 * checker. The checker is a copy of CCMSLib.Services.Domain WhatsAppTemplateRules (the API runs that one again before
 * "Submit to Meta"): keep the two in step.
 */

import type {
  WhatsAppTemplateDraft,
  WhatsAppTemplateCheckResult,
  WhatsAppTemplateCheckIssue,
  WhatsAppTemplateLibraryItem,
  WhatsAppTemplateParameterMapping,
  WhatsAppMessageField,
} from '@actschurch/ccms-sdk';

export const HEADER_MAX = 60;
export const BODY_MAX = 1024;
export const FOOTER_MAX = 60;
export const BUTTON_TEXT_MAX = 25;
export const MAX_EMOJI = 3;

const VAR_RX = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

export interface TextVariable {
  token: string;
  number: number | null;
  name: string | null;
  start: number;
  end: number;
}

/** Every {{...}} in order of appearance. */
export function variablesOf(text: string | null | undefined): TextVariable[] {
  const out: TextVariable[] = [];
  if (!text) return out;
  for (const m of text.matchAll(VAR_RX)) {
    const inner = m[1];
    const n = /^\d+$/.test(inner) ? parseInt(inner, 10) : null;
    out.push({ token: m[0], number: n, name: n === null ? inner : null, start: m.index ?? 0, end: (m.index ?? 0) + m[0].length });
  }
  return out;
}

/** Replaces {{n}} / {{name}} using the callback (null keeps the token). */
export function fillText(text: string | null | undefined, value: (v: TextVariable) => string | null | undefined): string {
  if (!text) return '';
  let out = '';
  let at = 0;
  for (const v of variablesOf(text)) {
    out += text.slice(at, v.start) + (value(v) ?? v.token);
    at = v.end;
  }
  return out + text.slice(at);
}

/** One line, no runs of spaces (what the worker sends to Meta). */
export function cleanValue(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

export function countEmoji(text: string | null | undefined): number {
  if (!text) return 0;
  let n = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp >= 0x1f3fb && cp <= 0x1f3ff) continue;
    if ((cp >= 0x1f000 && cp <= 0x1faff) || (cp >= 0x2600 && cp <= 0x27bf) || (cp >= 0x2b00 && cp <= 0x2bff) || (cp >= 0x2300 && cp <= 0x23ff)) n++;
  }
  return n;
}

export const hasLongDash = (text: string | null | undefined) => !!text && /[–—]/.test(text);

/** Any text as a Meta-valid template name ("Form Entry Received" → form_entry_received). */
export function normaliseName(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .trim()
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 512);
}

export const isValidName = (name: string | null | undefined) => !!name && /^[a-z0-9_]{1,512}$/.test(name) && /[a-z0-9]/.test(name);

const PROMOTIONAL = [
  'free', 'offer', 'offers', 'discount', 'discounts', 'sale', 'special', 'deal', 'deals', 'promo', 'promotion', 'limited time', 'buy', 'win',
  'prize', "don't miss", 'dont miss', 'hurry', 'exclusive', 'save', '% off', 'subscribe', 'shop', 'coupon', 'voucher', 'last chance', 'act now',
];

const US_SPELLINGS: Record<string, string> = {
  color: 'colour', colors: 'colours', center: 'centre', centers: 'centres', organization: 'organisation', organize: 'organise', organized: 'organised',
  favorite: 'favourite', behavior: 'behaviour', canceled: 'cancelled', canceling: 'cancelling', enroll: 'enrol', enrollment: 'enrolment',
  program: 'programme', programs: 'programmes', catalog: 'catalogue', honor: 'honour', neighbor: 'neighbour', traveling: 'travelling',
  apologize: 'apologise', realize: 'realise', recognize: 'recognise', finalize: 'finalise', authorize: 'authorise', authorized: 'authorised',
};

/** The creator's rule checker (errors block "Submit to Meta"; warnings do not). */
export function checkDraft(d: WhatsAppTemplateDraft): WhatsAppTemplateCheckResult {
  const errors: WhatsAppTemplateCheckIssue[] = [];
  const warnings: WhatsAppTemplateCheckIssue[] = [];
  const err = (part: string, code: string, message: string) => errors.push({ part, code, message });
  const warn = (part: string, code: string, message: string) => warnings.push({ part, code, message });

  if (!d.name?.trim()) err('name', 'name_required', 'Give the template a name.');
  else if (!isValidName(d.name)) err('name', 'name_format', `Use only lower case letters, digits and underscores in the name (for example ${normaliseName(d.name)}).`);

  const category = (d.category || '').toUpperCase();
  if (!['UTILITY', 'MARKETING', 'AUTHENTICATION'].includes(category)) err('category', 'category_required', 'Choose a category: Utility, Marketing or Authentication.');
  else if (category === 'AUTHENTICATION')
    err('category', 'authentication_not_supported', "Authentication templates use Meta's fixed code format, and CCMS sends sign-in codes by SMS and email only. Choose Utility.");
  else if (category === 'MARKETING') warn('category', 'marketing_opt_in', 'Marketing messages may only go to people who agreed to receive them, and Meta charges more for them.');

  if (!d.languageCode?.trim()) err('language', 'language_required', 'Choose a language.');

  const header = d.headerText ?? '';
  if (header.length > 0) {
    if (header.length > HEADER_MAX) err('header', 'header_length', `The header is ${header.length} characters; the most is ${HEADER_MAX}.`);
    if (/[\r\n]/.test(header)) err('header', 'header_newline', 'The header must be one line.');
    const hv = variablesOf(header);
    if (hv.length > 1) err('header', 'header_variables', 'The header may have one field at most.');
    if (hv.length === 1 && hv[0].number !== 1) err('header', 'header_variable_number', 'The header’s field must be {{1}}.');
    if (countEmoji(header) > 0) err('header', 'header_emoji', 'Meta does not allow emoji in the header.');
    if (/[*_~]/.test(header)) warn('header', 'header_formatting', 'Formatting marks (*, _ or ~) do not work in the header.');
  }

  const body = d.bodyText ?? '';
  const trimmed = body.trim();
  if (!trimmed) err('body', 'body_required', 'Write the message.');
  else {
    if (body.length > BODY_MAX) err('body', 'body_length', `The message is ${body.length} characters; the most is ${BODY_MAX}.`);
    const vars = variablesOf(trimmed);
    if (vars.length > 0) {
      if (vars[0].start === 0) err('body', 'body_starts_with_variable', 'The message must not start with a field. Start with a word, for example "Hi".');
      if (vars[vars.length - 1].end === trimmed.length) err('body', 'body_ends_with_variable', 'The message must not end with a field. End with a word or a full stop.');
      if (/\{\{\s*\w+\s*\}\}\s*\{\{\s*\w+\s*\}\}/.test(trimmed)) err('body', 'body_adjacent_variables', 'Two fields are side by side. Put a word between them.');
      if (vars.some((v) => v.name !== null)) err('body', 'body_named_variables', 'Use numbered fields ({{1}}, {{2}}, ...): tap a field chip to add one.');
      const inOrder = [...new Set(vars.filter((v) => v.number !== null).map((v) => v.number as number))];
      if (inOrder.length > 0 && (Math.min(...inOrder) !== 1 || Math.max(...inOrder) !== inOrder.length))
        err('body', 'body_variable_gaps', 'Number the fields {{1}}, {{2}}, ... with no gaps.');
      else if (inOrder.some((n, i) => i > 0 && n < inOrder[i - 1])) err('body', 'body_variable_order', 'Fields must appear in order: {{1}} first, then {{2}}, and so on.');
      const words = trimmed.replace(/\{\{[^}]*\}\}/g, ' ').split(/\s+/).filter(Boolean).length;
      if (words < inOrder.length * 3) warn('body', 'body_too_many_variables', 'The message has many fields for its length. Meta often rejects templates that are mostly fields: add more words.');
    }
    if (body.includes('\n\n\n')) warn('body', 'body_blank_lines', 'Use at most one blank line between paragraphs.');
    if (body.includes('    ')) err('body', 'body_spaces', 'Do not use more than 3 spaces in a row.');
    if (body.includes('\t')) err('body', 'body_tab', 'Do not use tabs.');
  }
  const emoji = countEmoji(body);
  if (emoji > MAX_EMOJI) err('body', 'body_emoji', `Use at most ${MAX_EMOJI} emoji (${emoji} now).`);

  const footer = d.footerText ?? '';
  if (footer.length > 0) {
    if (footer.length > FOOTER_MAX) err('footer', 'footer_length', `The footer is ${footer.length} characters; the most is ${FOOTER_MAX}.`);
    if (variablesOf(footer).length > 0) err('footer', 'footer_variables', 'The footer cannot have fields.');
    if (footer.includes('\n')) err('footer', 'footer_newline', 'The footer must be one line.');
  }

  const needed: { component: string; index: number }[] = [];
  variablesOf(header).forEach((v) => v.number !== null && needed.push({ component: 'header', index: v.number }));
  [...new Set(variablesOf(body).filter((v) => v.number !== null).map((v) => v.number as number))].forEach((n) => needed.push({ component: 'body', index: n }));
  for (const n of needed) {
    const dv = d.variables.find((x) => x.component.toLowerCase() === n.component && x.index === n.index);
    const label = `${n.component === 'header' ? 'Header ' : ''}{{${n.index}}}`;
    if (!dv?.field) warn('variables', 'variable_field', `${label} is not linked to a field of the message; link it after approval.`);
    if (!dv?.sample?.trim()) err('variables', 'variable_sample', `Give a sample value for ${label} (Meta reviews the template with it).`);
    else if (/[\n\t]/.test(dv.sample)) err('variables', 'variable_sample_line', `The sample for ${label} must be one line.`);
  }

  const buttons = d.buttons ?? [];
  if (buttons.length > 10) err('buttons', 'buttons_count', 'At most 10 buttons.');
  if (buttons.filter((b) => b.type === 'URL').length > 2) err('buttons', 'buttons_url_count', 'At most 2 link buttons.');
  if (buttons.filter((b) => b.type === 'PHONE_NUMBER').length > 1) err('buttons', 'buttons_phone_count', 'At most one call button.');
  buttons.forEach((b, i) => {
    const label = `Button ${i + 1}`;
    if (!b.text?.trim()) err('buttons', 'button_text', `${label} needs a label.`);
    else {
      if (b.text.length > BUTTON_TEXT_MAX) err('buttons', 'button_text_length', `${label}'s label is ${b.text.length} characters; the most is ${BUTTON_TEXT_MAX}.`);
      if (variablesOf(b.text).length > 0) err('buttons', 'button_text_variable', `${label}'s label cannot have a field.`);
      if (countEmoji(b.text) > 0) warn('buttons', 'button_text_emoji', `Leave emoji out of ${label}'s label.`);
    }
    if (b.type === 'URL') {
      const url = (b.url ?? '').trim();
      if (!/^https:\/\//i.test(url)) err('buttons', 'button_url', `${label}'s address must start with https://.`);
      const uv = variablesOf(url);
      if (uv.length > 1 || (uv.length === 1 && (uv[0].end !== url.length || uv[0].number !== 1)))
        err('buttons', 'button_url_variable', `${label}'s address may only end with {{1}} (the part that changes).`);
      if (uv.length === 1) {
        const prefix = url.slice(0, uv[0].start);
        if (!b.sample?.trim()) err('buttons', 'button_url_sample', `Give a full sample address for ${label}.`);
        else if (!b.sample.toLowerCase().startsWith(prefix.toLowerCase())) err('buttons', 'button_url_sample_prefix', `${label}'s sample must start with ${prefix}.`);
        if (!b.field) warn('buttons', 'button_url_field', `${label}'s link is not linked to a field of the message.`);
      }
    } else if (b.type === 'PHONE_NUMBER') {
      if (!/^\+?[0-9 ]{8,20}$/.test((b.phoneNumber ?? '').trim()))
        err('buttons', 'button_phone', `${label} needs a phone number in international format, for example +27 11 123 4567.`);
    } else if (b.type !== 'QUICK_REPLY') err('buttons', 'button_type', `${label}: choose Link, Quick reply or Call.`);
  });

  const allText = [header, body, footer, ...buttons.map((b) => b.text ?? '')].join('\n');
  if (category === 'UTILITY') {
    const lower = ` ${allText.toLowerCase().replace(/[^a-z0-9%' ]/g, ' ')} `;
    const found = PROMOTIONAL.filter((w) => lower.includes(` ${w} `) || (w.startsWith('%') && lower.includes(w)));
    if (found.length > 0)
      warn('body', 'utility_promotional', `Words like "${found.join('", "')}" make Meta treat a Utility template as Marketing. Utility is for confirmations and updates the person asked for.`);
  }
  if (hasLongDash(allText) || d.variables.some((v) => hasLongDash(v.sample))) err('body', 'long_dash', 'Replace the long dash with a comma, a colon or a full stop.');
  const seen = new Set<string>();
  for (const word of allText.match(/[A-Za-z-]+/g) ?? []) {
    const sa = US_SPELLINGS[word.toLowerCase()];
    if (sa && !seen.has(word.toLowerCase())) {
      seen.add(word.toLowerCase());
      warn('body', 'sa_english', `Use South African English: "${sa}" instead of "${word}".`);
    }
  }
  if (/[^\s,]+, [^\s,]+(?: [^\s,]+){0,2}, and\b/i.test(allText)) warn('body', 'oxford_comma', 'Leave out the comma before "and" at the end of a list.');

  return { errors, warnings, ok: errors.length === 0 };
}

// ------------------------------------------------------------------ previews and mapping

export interface PreviewParts {
  headerText: string | null;
  bodyText: string;
  footerText: string | null;
  buttons: { type: string; text: string; url?: string | null }[];
}

/**
 * A linked template filled with sample values from the mapping: what the person would see. A variable with no field
 * and no default shows as its token.
 */
export function previewLinked(
  template: WhatsAppTemplateLibraryItem,
  mapping: WhatsAppTemplateParameterMapping[],
  fields: WhatsAppMessageField[],
  samples?: Record<string, string>,
): PreviewParts {
  const value = (component: string, v: TextVariable) => {
    const m = mapping.find((x) => x.component === component && (v.name ? x.name === v.name : x.index === v.number));
    if (!m) return null;
    const f = fields.find((x) => x.key.toLowerCase() === (m.field || '').toLowerCase());
    const sample = cleanValue((m.field && samples?.[m.field]) || f?.sample || m.defaultValue || '');
    return sample || (m.field ? `[${f?.label ?? m.field}]` : null);
  };
  return {
    headerText: template.headerText ? fillText(template.headerText, (v) => value('header', v)) : null,
    bodyText: fillText(template.bodyText ?? '', (v) => value('body', v)),
    footerText: template.footerText ?? null,
    buttons: template.buttons.map((b) => ({ type: b.type, text: b.text, url: b.url })),
  };
}

/** The creator's draft as a preview, with the variables' samples filled in. */
export function previewDraft(d: WhatsAppTemplateDraft): PreviewParts {
  const sample = (component: string, n: number | null) => d.variables.find((v) => v.component === component && v.index === n)?.sample || null;
  return {
    headerText: d.headerText ? fillText(d.headerText, (v) => sample('header', v.number)) : null,
    bodyText: fillText(d.bodyText, (v) => sample('body', v.number)),
    footerText: d.footerText || null,
    buttons: d.buttons.map((b) => ({ type: b.type, text: b.text, url: b.url })),
  };
}

/** Status chip colour from the lookup (falls back on the status name). */
export function statusColor(code: string | undefined | null, color?: string | null): string {
  if (color) return color;
  switch ((code ?? '').toUpperCase()) {
    case 'APPROVED':
      return 'green';
    case 'PENDING':
      return 'yellow';
    case 'REJECTED':
      return 'red';
    case 'PAUSED':
      return 'orange';
    default:
      return 'gray';
  }
}

export const LINK_STATE_UI: Record<string, { label: string; color: string }> = {
  active: { label: 'Linked', color: 'green' },
  waiting: { label: 'Waiting for Meta', color: 'yellow' },
  blocked: { label: 'Template blocked', color: 'red' },
  missing: { label: 'Template missing', color: 'red' },
  incomplete: { label: 'Fields not mapped', color: 'orange' },
  none: { label: 'Not linked', color: 'gray' },
};
